import {
    autoUpdate,
    FloatingPortal,
    flip,
    offset,
    shift,
    useFloating,
} from "@floating-ui/react";
import { faCog, faLoader } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Popover } from "@headlessui/react";
import * as d3 from "d3";
import { type ReactNode, useMemo, useRef, useState } from "react";
import { useResizeObserver } from "../../../../../ui/Plot/hooks";

// Utils
const countFormat = Intl.NumberFormat("en");
const needsLightText = (color: string) => (d3.lab(color).l ?? 100) < 65;

// Components
interface SettingsDropdownProps {
    children: ReactNode;
    button: "sm" | "lg";
}

export const SettingsDropdown = (props: SettingsDropdownProps) => {
    const { refs, floatingStyles } = useFloating({
        strategy: "fixed",
        placement: "right-start",
        middleware: [
            offset({
                mainAxis: props.button === "sm" ? 10 : 6,
                crossAxis: props.button === "sm" ? -8 : 0,
            }),
            shift(),
        ],
        whileElementsMounted: autoUpdate,
    });

    return (
        <div ref={refs.setReference}>
            <Popover>
                <Popover.Button
                    className={
                        props.button === "sm"
                            ? "flex items-center mr-2"
                            : "w-6 h-6 bg-white rounded px-1 py-0.5 text-sm flex items-center justify-center gap-1 border hover:bg-ae-blue-40 ui-selected:border-ae-blue-550 ui-selected:text-ae-blue-550"
                    }
                >
                    <FontAwesomeIcon icon={faCog} className="w-3" />
                </Popover.Button>
                <Popover.Panel
                    ref={refs.setFloating}
                    style={floatingStyles}
                    className="overflow-auto rounded-md p-1 py-1 border-2 border-ae-slate-300 z-50 bg-white"
                >
                    {props.children}
                </Popover.Panel>
            </Popover>
        </div>
    );
};

interface SettingsDropdownProviderProps {
    children: ReactNode;
}

export const SettingsDropdownProvider = (
    props: SettingsDropdownProviderProps,
) => {
    const { refs, floatingStyles } = useFloating({
        strategy: "fixed",
        placement: "right-start",
        middleware: [
            offset({
                mainAxis: 10,
                crossAxis: -12,
            }),
            shift(),
        ],
        whileElementsMounted: autoUpdate,
    });

    return (
        <div ref={refs.setReference}>
            <Popover>
                <Popover.Button className="flex items-center mr-2">
                    <FontAwesomeIcon icon={faCog} className="w-3" />
                </Popover.Button>
                <Popover.Panel
                    ref={refs.setFloating}
                    style={floatingStyles}
                    className="min-w-44 rounded-md p-1 py-1 border-2 border-ae-slate-300 z-50 bg-white"
                >
                    {props.children}
                </Popover.Panel>
            </Popover>
        </div>
    );
};

const LoadingOverlay = () => (
    <div className="absolute h-full w-full text-ae-slate-600 bg-white bg-opacity-60 flex items-center justify-center">
        <FontAwesomeIcon icon={faLoader} className="h-4 w-4 animate-spin" />
    </div>
);

/** Wrap around with `%` when there are more categories than colours. */
export const CATEGORY_COLORS = [
    "#355F73",
    "#6A9FB5",
    "#B8D8E3",
    "#4F772D",
    "#90A955",
    "#ECF39E",
    "#E3B23C",
    "#BC6C25",
    "#9E2A2B",
    "#7A3E3E",
] as const;

export interface CountsCardCategory {
    key: string;
    label: string;
    count: number;
    color: string;
}

interface CountsCardViewProps {
    data: CountsCardCategory[];
    loading: boolean;
    /** Trails the total, e.g. "non-detects in view". */
    totalLabel: string;
}

/** Stacked bar over a legend of counts and shares, re-used by other cards. */
export const CountsCardView = (props: CountsCardViewProps) => {
    const [hoveredKey, setHoveredKey] = useState<string | null>(null);

    const total = useMemo(() => {
        if (!props.data) {
            return 0;
        }
        let total = 0;
        for (const i of props.data) {
            total += i.count;
        }
        return total;
    }, [props.data]);

    // Empty categories stay in the legend but drop out of the bar.
    const segments = (props.data || []).filter((d) => d.count > 0);
    const faded = (key: string) => hoveredKey !== null && hoveredKey !== key;

    return (
        <>
            {!props.data || props.data.length === 0 ? (
                <div className="text-xs text-ae-slate-600 py-4 text-center">
                    No data to display.
                </div>
            ) : (
                <div className="relative">
                    {props.loading && <LoadingOverlay />}
                    <div className="mt-1">
                        <span className="text-2xl">
                            {Intl.NumberFormat("en").format(total)}
                        </span>
                        <span className="ml-1 text-sm text-ae-slate-600">
                            {props.totalLabel}
                        </span>
                    </div>
                    <div className="mt-2 flex h-3 w-full overflow-hidden rounded-full bg-ae-slate-200">
                        {segments.map((d) => (
                            <div
                                key={d.key}
                                className="h-full transition-opacity duration-150"
                                style={{
                                    width: `${(d.count / total) * 100}%`,
                                    backgroundColor: d.color,
                                    opacity: faded(d.key) ? 0.3 : 1,
                                }}
                                onMouseEnter={() => setHoveredKey(d.key)}
                                onMouseLeave={() => setHoveredKey(null)}
                            />
                        ))}
                    </div>
                    <div className="mt-2 flex flex-col">
                        {props.data.map((d) => (
                            <div
                                key={d.key}
                                className={`flex items-center gap-2 rounded px-1 py-1 transition-colors ${
                                    hoveredKey === d.key
                                        ? "bg-ae-slate-100"
                                        : ""
                                }`}
                                onMouseEnter={() => setHoveredKey(d.key)}
                                onMouseLeave={() => setHoveredKey(null)}
                            >
                                <span
                                    className="h-3 w-3 shrink-0 rounded-sm transition-opacity duration-150"
                                    style={{
                                        backgroundColor: d.color,
                                        opacity: faded(d.key) ? 0.3 : 1,
                                    }}
                                />
                                <span className="flex-1 truncate text-xs text-ae-slate-900">
                                    {d.label}
                                </span>
                                <span className="text-xs font-bold tabular-nums text-ae-slate-900">
                                    {Intl.NumberFormat("en").format(d.count)}
                                </span>
                                <span className="w-12 text-right text-xs tabular-nums text-ae-slate-600">
                                    {total > 0
                                        ? `${((d.count / total) * 100).toFixed(1)}%`
                                        : "—"}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </>
    );
};

export interface TreemapCardCategory {
    key: string;
    label: string;
    count: number;
}

interface TreemapCardViewProps {
    data: TreemapCardCategory[];
    loading: boolean;
    totalLabel: string;
    valueSuffix?: string;
}

// Treemap view settings
const MIN_TILE_WIDTH = 44;
const MIN_TILE_HEIGHT = 28;
const PLOT_HEIGHT = 200;
const GROUP_BELOW_SHARE = 0.01;
const GROUPED_TILE_KEY = "__grouped_tail__";
type HoveredTile = TreemapCardCategory & { members?: TreemapCardCategory[] };

/**
 * Treemap of the categories by share of the total, re-used by other cards.
 * Categories too small to read collapse into one tile, named on hover.
 */
export const TreemapCardView = (props: TreemapCardViewProps) => {
    const containerRef = useRef(null);
    const dimensions = useResizeObserver(containerRef);
    const [hovered, setHovered] = useState<HoveredTile | null>(null);

    const { refs, floatingStyles } = useFloating({
        strategy: "fixed",
        placement: "top-start",
        middleware: [offset(12), flip(), shift({ padding: 8 })],
        whileElementsMounted: autoUpdate,
    });

    // Anchor the tooltip to cursor.
    const trackPointer = (event: { clientX: number; clientY: number }) => {
        const { clientX: x, clientY: y } = event;
        refs.setPositionReference({
            getBoundingClientRect: () => ({
                width: 0,
                height: 0,
                x,
                y,
                left: x,
                right: x,
                top: y,
                bottom: y,
            }),
        });
    };

    const { tiles, total } = useMemo(() => {
        // Sorted so the grouped tile lists its members largest first.
        const categories = (props.data || [])
            .filter((d) => d.count > 0)
            .sort((a, b) => b.count - a.count);
        const total = categories.reduce((sum, d) => sum + d.count, 0);

        if (dimensions.width === 0 || categories.length === 0) {
            return { tiles: [], total };
        }

        // Group members with smallest numbers
        const isTail = (d: TreemapCardCategory) =>
            d.count / total < GROUP_BELOW_SHARE;
        const tail = categories.filter(isTail);
        const grouped: TreemapCardCategory[] =
            tail.length > 1
                ? [
                      ...categories.filter((d) => !isTail(d)),
                      {
                          key: GROUPED_TILE_KEY,
                          label: `${tail.length} smaller types`,
                          count: tail.reduce((sum, d) => sum + d.count, 0),
                      },
                  ]
                : categories;

        type TreemapNode = Partial<TreemapCardCategory> & {
            children?: TreemapCardCategory[];
        };

        const hierarchy = d3
            .hierarchy<TreemapNode>({ children: grouped })
            .sum((d) => (d.children ? 0 : (d.count ?? 0)))
            .sort((a, b) => (b.value ?? 0) - (a.value ?? 0));

        const root = d3
            .treemap<TreemapNode>()
            .size([dimensions.width, PLOT_HEIGHT])
            .paddingInner(2)
            .round(true)(hierarchy);

        const leaves = root.leaves();
        return {
            total,
            tiles: leaves.map((leaf, i) => {
                const data = leaf.data as TreemapCardCategory;
                const width = leaf.x1 - leaf.x0;
                const tileHeight = leaf.y1 - leaf.y0;
                const color = CATEGORY_COLORS[i % CATEGORY_COLORS.length];

                return {
                    ...data,
                    x: leaf.x0,
                    y: leaf.y0,
                    width,
                    height: tileHeight,
                    color,
                    darkFill: needsLightText(color),
                    showLabel:
                        width >= MIN_TILE_WIDTH &&
                        tileHeight >= MIN_TILE_HEIGHT,
                    members: data.key === GROUPED_TILE_KEY ? tail : undefined,
                };
            }),
        };
    }, [props.data, dimensions.width]);

    const formatValue = (count: number) =>
        `${countFormat.format(count)}${
            props.valueSuffix ? ` ${props.valueSuffix}` : ""
        }`;

    // Callers are responsible for the empty state: bailing out here would unmount
    // the container the resize observer measures, leaving the treemap at width 0.
    return (
        <div className="relative">
            {props.loading && <LoadingOverlay />}
            <div
                ref={containerRef}
                className="relative mt-2 w-full"
                style={{ height: PLOT_HEIGHT }}
            >
                {tiles.map((d) => (
                    <div
                        key={d.key}
                        onMouseEnter={(event) => {
                            trackPointer(event);
                            setHovered(d);
                        }}
                        onMouseMove={trackPointer}
                        onMouseLeave={() => setHovered(null)}
                        className={`absolute overflow-hidden rounded-sm px-1.5 py-1 transition-opacity duration-150 ${
                            d.darkFill ? "text-white" : "text-ae-slate-900"
                        }`}
                        style={{
                            left: d.x,
                            top: d.y,
                            width: d.width,
                            height: d.height,
                            backgroundColor: d.color,
                            opacity:
                                hovered && hovered.key !== d.key ? 0.45 : 1,
                        }}
                    >
                        {d.showLabel && (
                            <span className="block truncate text-[11px] leading-tight">
                                {d.label}
                            </span>
                        )}
                    </div>
                ))}
            </div>
            {hovered && (
                <FloatingPortal>
                    <div
                        ref={refs.setFloating}
                        style={floatingStyles}
                        className="z-50 pointer-events-none max-w-64 rounded-lg border border-ae-gray-300 bg-white px-2 py-1 text-xs text-ae-slate-900 shadow-lg"
                    >
                        <div className="font-bold">{hovered.label}</div>
                        <div className="text-ae-slate-600">
                            {`${formatValue(hovered.count)} · ${(
                                (hovered.count / total) * 100
                            ).toFixed(1)}%`}
                        </div>
                        {hovered.members && (
                            <div className="mt-1 border-t border-ae-gray-300 pt-1">
                                {hovered.members.map((m) => (
                                    <div
                                        key={m.key}
                                        className="flex justify-between gap-3"
                                    >
                                        <span className="truncate text-ae-slate-600">
                                            {m.label}
                                        </span>
                                        <span className="tabular-nums">
                                            {formatValue(m.count)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </FloatingPortal>
            )}
            <div className="mt-2 text-xs text-ae-slate-600">
                {countFormat.format(total)} {props.totalLabel}
            </div>
        </div>
    );
};
